import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Save, Plus, Minus, Search, Package, Calendar, Clock, Loader, User, MessageSquare } from 'lucide-react';
import CustomDatePicker from '../shared/CustomDatePicker';
import { updateOrder, reassignOrder } from '../../../../services/api/salesOrderApi';
import { getAllProducts } from '../../../../services/api/productApi';
import { getAllUsers } from '../../../../services/api/userApi';
import { getDeliverySchedules } from '../../../../services/api/deliveryScheduleApi';
import { format } from 'date-fns';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2
    }).format(amount || 0);
};

const EditOrderModal = ({ order, isOpen, onClose, onSuccess, currentRole }) => {
    const [items, setItems] = useState([]);
    const [deliveryDate, setDeliveryDate] = useState('');
    const [timeSlot, setTimeSlot] = useState('');
    const [notes, setNotes] = useState('');
    const [assignedSalesPersonId, setAssignedSalesPersonId] = useState('');
    const [salesPersons, setSalesPersons] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Delivery schedules from API
    const [deliverySchedules, setDeliverySchedules] = useState([]);
    const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

    // Roles that can reassign
    const canReassign = ['TENANT_ADMIN', 'TENANT_OWNER', 'SALES_MANAGER', 'SYSTEM_ADMIN'].includes(currentRole);

    // Product search state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);

    useEffect(() => {
        if (isOpen && order) {
            // Initialize state from order
            setItems(order.items.map(item => ({
                productPublicId: item.productPublicId || item.id,
                name: item.name || item.productName,
                quantity: item.quantity,
                unitPrice: item.price || item.unitPrice,
                stock: null,
                warehouseQuantity: item.warehouseQuantity,
            })));

            if (order.deliveryDate) {
                try {
                    setDeliveryDate(format(new Date(order.deliveryDate), 'yyyy-MM-dd'));
                } catch (e) {
                    console.error("Invalid date format", e);
                }
            } else {
                setDeliveryDate('');
            }
            setTimeSlot(order.timeSlot || '');
            setNotes(order.notes || '');
            setAssignedSalesPersonId(order.assignedSalesPersonPublicId || '');
            setError(null);
            setSearchTerm('');
            setSearchResults([]);
            setShowResults(false);
        }
    }, [isOpen, order]);

    // Fetch sales persons list for reassignment
    useEffect(() => {
        if (isOpen && canReassign && salesPersons.length === 0) {
            getAllUsers({ size: 1000 })
                .then(data => {
                    const all = data.content || data || [];
                    setSalesPersons(all.filter(u =>
                        u.realmRoles?.some(r => ['SALES_PERSON', 'SALES_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER'].includes(r.replace('ROLE_', '')))
                    ));
                })
                .catch(err => console.error('Failed to load users', err));
        }
    }, [isOpen, canReassign]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch delivery schedules when modal opens
    useEffect(() => {
        if (isOpen && deliverySchedules.length === 0) {
            setIsLoadingSchedules(true);
            getDeliverySchedules()
                .then(data => setDeliverySchedules(data || []))
                .catch(err => console.error('Failed to load delivery schedules', err))
                .finally(() => setIsLoadingSchedules(false));
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            if (!searchTerm.trim()) {
                setSearchResults([]);
                return;
            }
            setIsSearching(true);
            try {
                const result = await getAllProducts({ search: searchTerm, size: 10 });
                setSearchResults(result.content || []);
                setShowResults(true);
            } catch (err) {
                console.error("Failed to search products", err);
            } finally {
                setIsSearching(false);
            }
        };

        const timer = setTimeout(fetchProducts, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const subtotalExVat = useMemo(() => {
        return items.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (item.unitPrice || 0)), 0);
    }, [items]);

    const totalVat = useMemo(() => {
        return subtotalExVat * 0.05;
    }, [subtotalExVat]);

    const totalAmount = useMemo(() => {
        return subtotalExVat * 1.05;
    }, [subtotalExVat]);

    const handleQuantityChange = (index, delta) => {
        const newItems = [...items];
        const currentQty = parseFloat(newItems[index].quantity);
        const newQuantity = Math.max(1, (isNaN(currentQty) ? 0 : currentQty) + delta);
        newItems[index].quantity = String(newQuantity);
        setItems(newItems);
    };

    const handleQuantityInput = (index, value) => {
        const newItems = [...items];
        newItems[index].quantity = value;
        setItems(newItems);
    };

    const handleRemoveItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const getProductUnitPrice = (product) => {
        if (!order?.customer?.priceType) {
            return product.effectivePrice || product.retailPrice || product.price || 0;
        }
        const isWholesale = order.customer.priceType === 'WHOLESALE';
        const regularPrice = isWholesale ? (product.price || 0) : (product.retailPrice || 0);
        const discountedPrice = isWholesale ? product.discountedPrice : product.discountedRetailPrice;
        const finalPrice = (discountedPrice !== null && discountedPrice !== undefined && discountedPrice > 0)
            ? discountedPrice
            : regularPrice;
        return finalPrice || 0;
    };

    const handleAddProduct = (product) => {
        const existingIndex = items.findIndex(i => i.productPublicId === product.id);
        if (existingIndex >= 0) {
            handleQuantityChange(existingIndex, 1);
        } else {
            setItems([...items, {
                productPublicId: product.id,
                name: product.name,
                quantity: 1,
                unitPrice: getProductUnitPrice(product),
                stock: product.stock
            }]);
        }
        setSearchTerm('');
        setShowResults(false);
    };

    const handleSave = async () => {
        if (items.length === 0) {
            setError("Order must have at least one item.");
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            const orderId = order.id || order.publicId;

            // Reassign sales person if changed
            if (canReassign && assignedSalesPersonId && assignedSalesPersonId !== order.assignedSalesPersonPublicId) {
                await reassignOrder(orderId, { assignedSalesPersonPublicId: assignedSalesPersonId });
            }

            const payload = {
                items: items.map(item => ({
                    productPublicId: item.productPublicId,
                    quantity: Math.max(1, parseFloat(item.quantity) || 1)
                })),
                deliveryDate: deliveryDate || null,
                timeSlot: timeSlot || null,
                notes: notes || null
            };

            const updatedOrder = await updateOrder(orderId, payload);
            void updatedOrder; // parent refreshes via fetchOrders()
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update order. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !order) return null;

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">
                
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-white">
                    <div>
                        <h2 className="text-lg font-black text-gray-900">Edit Order {order.orderRef}</h2>
                        <p className="text-xs font-medium text-gray-500 mt-0.5">Modify items, quantities, and delivery details</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                        <X size={16} strokeWidth={3} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 space-y-6">
                    
                    {error && (
                        <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-sm font-bold border border-red-100 flex items-start gap-3">
                            <X size={16} className="mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Delivery Details — always 2 columns */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                        <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-2">Delivery Details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Calendar size={14} className="text-gray-400" /> Date
                                </label>
                                <CustomDatePicker
                                    mode="single"
                                    value={deliveryDate}
                                    onChange={(val) => setDeliveryDate(val)}
                                    quickFilters={false}
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Clock size={14} className="text-gray-400" /> Time Slot
                                </label>
                                <select
                                    value={timeSlot}
                                    onChange={(e) => setTimeSlot(e.target.value)}
                                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                    disabled={isLoadingSchedules}
                                >
                                    {isLoadingSchedules ? (
                                        <option value="">Loading...</option>
                                    ) : deliverySchedules.length > 0 ? (
                                        deliverySchedules.map(schedule => (
                                            <option key={schedule.publicId} value={schedule.name}>
                                                {schedule.name}{schedule.startTime && schedule.endTime ? ` (${schedule.startTime} – ${schedule.endTime})` : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="">Any Time</option>
                                            <option value="Morning">Morning</option>
                                            <option value="Afternoon">Afternoon</option>
                                            <option value="Evening">Evening</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-1.5">
                                <MessageSquare size={14} className="text-gray-400" /> Special Instructions
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add landmarks, specific delivery instructions or notes here..."
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none min-h-[60px]"
                                maxLength={1000}
                            />
                        </div>
                    </div>

                    {/* Assigned Sales Person — admins / managers only */}
                    {canReassign && (
                        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-3">Assigned Sales Person</h3>
                            <div className="relative">
                                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                <select
                                    value={assignedSalesPersonId}
                                    onChange={e => setAssignedSalesPersonId(e.target.value)}
                                    className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none"
                                >
                                    <option value="">— Unassigned —</option>
                                    {salesPersons.map(u => (
                                        <option key={u.publicId} value={u.publicId}>
                                            {u.firstName} {u.lastName} ({u.username})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Items */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col h-full">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Order Items</h3>
                            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg">
                                {items.length} Items
                            </span>
                        </div>

                        {/* Product Search */}
                        <div className="relative mb-4" ref={searchRef}>
                            <div className="relative">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Search products to add..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        if (e.target.value) setShowResults(true);
                                    }}
                                    onFocus={() => {
                                        if (searchTerm) setShowResults(true);
                                    }}
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                />
                                {isSearching && (
                                    <Loader className="absolute right-3.5 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" size={16} />
                                )}
                            </div>

                            {/* Search Results Dropdown */}
                            {showResults && searchTerm && !isSearching && (
                                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden z-10 max-h-60 overflow-y-auto">
                                    {searchResults.length === 0 ? (
                                        <div className="p-4 text-center text-sm text-gray-500">No products found.</div>
                                    ) : (
                                        searchResults.map(product => (
                                            <button
                                                key={product.id}
                                                onClick={() => handleAddProduct(product)}
                                                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between border-b border-gray-50 last:border-0"
                                            >
                                                <div className="flex-1 min-w-0 pr-4">
                                                    <p className="text-sm font-bold text-gray-900 break-words whitespace-normal">{product.name}</p>
                                                    <p className="text-xs text-gray-500">Stock: {product.stock}</p>
                                                </div>
                                                <div className="flex-shrink-0 text-right">
                                                    <span className="text-sm font-black text-blue-600">{formatCurrency(getProductUnitPrice(product))}</span>
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Items List */}
                        <div className="space-y-3 mt-2">
                            {items.map((item, index) => (
                                <div key={index} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                                    <div className="flex-1 min-w-0 flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                                            <Package size={18} className="text-gray-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-gray-900 break-words whitespace-normal">{item.name}</p>
                                            <p className="text-xs font-black text-gray-500">{formatCurrency(item.unitPrice)}</p>
                                            {item.warehouseQuantity !== undefined && item.warehouseQuantity !== null && item.warehouseQuantity !== item.quantity && (
                                                <p className="text-[10px] font-black text-amber-600 uppercase tracking-tighter mt-1">
                                                    Warehouse Suggestion: {item.warehouseQuantity}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="flex-shrink-0 flex items-center justify-between md:justify-end gap-4 w-full md:w-auto border-t md:border-0 pt-2 md:pt-0 border-gray-100/70">
                                        {/* Quantity Controls */}
                                        <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden h-9">
                                            <button 
                                                onClick={() => handleQuantityChange(index, -1)}
                                                disabled={(parseFloat(item.quantity) || 0) <= 1}
                                                className="w-8 h-full flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-30 transition-colors"
                                            >
                                                <Minus size={14} strokeWidth={3} />
                                            </button>
                                            <input 
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => handleQuantityInput(index, e.target.value)}
                                                className="min-w-[72px] w-20 px-1 h-full text-center text-sm font-bold border-x border-gray-100 focus:outline-none focus:bg-blue-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                min="1"
                                            />
                                            <button 
                                                onClick={() => handleQuantityChange(index, 1)}
                                                className="w-8 h-full flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                                            >
                                                <Plus size={14} strokeWidth={3} />
                                            </button>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="w-20 text-right font-black text-gray-900">
                                                {formatCurrency((parseFloat(item.quantity) || 0) * (item.unitPrice || 0))}
                                            </div>

                                            <button 
                                                onClick={() => handleRemoveItem(index)}
                                                className="w-8 h-8 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors"
                                                title="Remove Item"
                                            >
                                                <X size={16} strokeWidth={3} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {items.length === 0 && (
                                <div className="text-center py-8 text-gray-400 text-sm font-medium">
                                    No items in this order. Search and add products above.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest">New Total (Incl. VAT)</p>
                        <p className="text-2xl font-black text-blue-600 drop-shadow-sm">{formatCurrency(totalAmount)}</p>
                        <span className="text-[10px] font-bold text-gray-400">
                            Excl. VAT: {formatCurrency(subtotalExVat)} | VAT (5%): {formatCurrency(totalVat)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={onClose}
                            disabled={isSaving}
                            className="px-6 py-2.5 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSave}
                            disabled={isSaving || items.length === 0}
                            className="px-6 py-2.5 rounded-xl text-sm font-black text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100"
                        >
                            {isSaving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditOrderModal;
